import * as React from "react";
import { useEffect, useState } from "react";

import {
  Box,
  TextField as MuiTextField,
  MenuItem, Select, InputLabel, FormControl
} from "@mui/material";
import Grid from "@mui/material/Grid";
import {
  Datagrid, FunctionField,
  Labeled,
  ListContextProvider,
  Show,
  SimpleShowLayout,
  TextField,
  useDataProvider,
  useShowController
} from "react-admin";

import { CollectionPartner, CollectionPartnerType } from "../../components/collections/types";
import { CollectionPartnerStockBalance, StockItemType } from "../../components/stock/types";
import { Resources } from "../../resources";
import {
  countryNameRenderer,
  enumIconRenderer, enumRenderer,
  geolocationRenderer
} from "../../utils/field-renderers";
import { stockItemIcons } from "../stock-items/renders";

const useListContext = (data: any[], filteredData: any[], sortField: string, sortOrder: string) => {
  const sort = sortField ? { field: sortField, order: sortOrder } : {};
  return {
    data: filteredData,
    total: filteredData.length,
    page: 1,
    perPage: filteredData.length,
    filterValues: {},
    displayedFilters: {},
    sort: sort
  } as any;
};

export const StockBalanceShow: React.FC = () => {
  const { record: stockBalance } = useShowController<CollectionPartnerStockBalance>();
  const dataProvider = useDataProvider();
  const [collectionPartner, setCollectionPartner] = useState<CollectionPartner | null>(null);
  const [sortField, setSortField] = useState("name");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");
  const [typeFilter, setTypeFilter] = useState<string[]>([]);
  const [nameFilter, setNameFilter] = useState<string>("");

  useEffect(() => {
    if (stockBalance?.id) {
      dataProvider.getOne(Resources.CollectionPartners, { id: stockBalance.id })
        .then(({ data }) => setCollectionPartner(data));
    }
  }, [stockBalance, dataProvider]);

  const handleTypeChange = (event: any) => setTypeFilter(event.target.value);
  const handleNameChange = (event: React.ChangeEvent<HTMLInputElement>) => setNameFilter(event.target.value);

  const stockBalancesData = Object.entries(stockBalance?.stockBalances || {}).flatMap(([type, balances]) =>
    balances.map((stockBalance) => ({ ...stockBalance, type }))
  );

  const groupedAndSortedData = Object.entries(stockBalance?.stockBalances || {}).flatMap(([type, balances]) => {
    const filteredBalances = balances
      .filter((item) =>
        (type === "PELLETS" || item.quantity !== 0) &&
        (typeFilter.length === 0 || typeFilter.includes(type)) &&
        item.name.toLowerCase().includes(nameFilter.toLowerCase())
      )
      .sort((a, b) => {
        const aVal = a.name.toLowerCase();
        const bVal = b.name.toLowerCase();
        return sortOrder === "asc"
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal);
      });

    return filteredBalances.map((item) => ({
      ...item,
      type
    }));
  });

  const listContext = useListContext(stockBalancesData, groupedAndSortedData, sortField, sortOrder);

  return (
    <Show queryOptions={{ cacheTime: 0, staleTime: 0 }}>
      <SimpleShowLayout>
        {collectionPartner && (
          <Grid container spacing={2}>
            <Grid item xs={4}>
              <Labeled>
                <TextField source="code" label="Code" record={collectionPartner} />
              </Labeled>
            </Grid>
            <Grid item xs={4}>
              <Labeled>
                <TextField source="name" label="Name" record={collectionPartner} />
              </Labeled>
            </Grid>
            <Grid item xs={4}>
              <Labeled>
                <FunctionField label="Type"
                  render={() => enumRenderer(collectionPartner.type, CollectionPartnerType)}
                />
              </Labeled>
            </Grid>
            <Grid item xs={4}>
              <Labeled>
                <FunctionField label="Country" render={() => countryNameRenderer(collectionPartner)}
                />
              </Labeled>
            </Grid>
            <Grid item xs={4}>
              <Labeled>
                <TextField source="address" label="Address" record={collectionPartner} />
              </Labeled>
            </Grid>
            <Grid item xs={4}>
              <Labeled>
                <FunctionField label="Geolocation"
                  render={() => geolocationRenderer(collectionPartner)}
                />
              </Labeled>
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ ml: 2, mt: 2 }}>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <MuiTextField
                    label="Filter by Item Name"
                    value={nameFilter}
                    onChange={handleNameChange}
                    variant="outlined"
                    size="small"
                  />
                  <Box sx={{ ml: 2 }} />
                  <FormControl variant="outlined" size="small" sx={{ minWidth: 200 }}>
                    <InputLabel>Filter by Type</InputLabel>
                    <Select
                      label="Filter by Type"
                      value={typeFilter}
                      onChange={handleTypeChange}
                      multiple
                    >
                      {Object.keys(StockItemType).map((key) => (
                        <MenuItem key={key} value={key}>
                          {enumRenderer(key, StockItemType)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12}>
              <ListContextProvider value={listContext}>
                <Datagrid>
                  <FunctionField
                    label={
                      <span
                        style={{ cursor: "pointer", fontWeight: "bold" }}
                        onClick={() => {
                          if (sortField === "name") {
                            setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
                          } else {
                            setSortField("name");
                            setSortOrder("asc");
                          }
                        }}
                      >
      Name {sortField === "name" ? (sortOrder === "asc" ? "↑" : "↓") : ""}
                      </span>
                    }
                    render={(record: any) => record.name}
                  />
                  <TextField source="quantity" label="Quantity" />
                  <FunctionField label="Type"
                    source= "type"
                    render={(record: { type: StockItemType }) =>
                      enumIconRenderer(record.type, StockItemType, stockItemIcons)}
                  />
                </Datagrid>
              </ListContextProvider>
            </Grid>
          </Grid>
        )}
      </SimpleShowLayout>
    </Show>
  );
};